import React, { useEffect, useState } from 'react';
import './Message.scss';
import { Form, Formik, FormikHelpers, FormikProps, FormikState } from 'formik';
import { SalvButton, SalvDropdown, SalvModal, Size } from '@salvapp/ui-components';
import * as Yup from 'yup';
import './AddOrganisationToConversationModal.scss';
import { useDispatch } from 'react-redux';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { BridgeApiConversation, BridgeApiFriendOrganisationV2 } from '../api-internal';
import { bridgeOrganisationApi, conversationApi } from '../apis';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { faWarning } from '@fortawesome/pro-solid-svg-icons';
import LoadingOverlay from '../feedback/LoadingOverlay';

const validationSchema = Yup.object().shape(
  {
    organisation: Yup.object().shape({
      key: Yup.string(),
      label: Yup.string().required('Required'),
      value: Yup.string().required('Required'),
    }),
  },
  []
);

export interface AddOrganisationToConversationFormValues {
  organisation: DropdownOption | undefined;
}

const convertForSelect = (name: string, id: string) => {
  return { label: name, key: id, value: id };
};

const AddOrganisationToConversationModal = ({
  conversation,
  showModal,
  setShowModal,
  onUpdate,
}: {
  conversation: BridgeApiConversation;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  onUpdate: (updated: boolean) => void;
}): React.JSX.Element => {
  const dispatch = useDispatch();

  const [organisations, setOrganisations] = useState<BridgeApiFriendOrganisationV2[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const initialValues: AddOrganisationToConversationFormValues = {
    organisation: undefined,
  };

  const initialFormState: Partial<FormikState<AddOrganisationToConversationFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };
  useEffect(() => {
    async function fetchOrganisationThatCanBeAddedToConversation() {
      try {
        setLoading(true);
        const friends = await bridgeOrganisationApi.getAllForwardableFriendOrganisations();
        setOrganisations(
          friends.content.filter(
            (friend) =>
              !conversation.counterparties.some((counterparty) => counterparty.organisationHandle === friend.id)
          )
        );
      } finally {
        setLoading(false);
      }
    }

    if (showModal) {
      fetchOrganisationThatCanBeAddedToConversation();
    }
  }, [conversation, showModal]);

  const confirm = (
    values: AddOrganisationToConversationFormValues,
    helpers: FormikHelpers<AddOrganisationToConversationFormValues>
  ) => {
    setShowModal(false);
    const organisationName = values.organisation!.label;

    dispatch(
      showConfirmationModal({
        title: `Are you sure you want to add ${organisationName} to this conversation?`,
        description: `${organisationName} will see all the messages in this conversation.`,
        confirmationText: 'Yes',
        cancelText: 'No, go back',
        icon: faWarning,
        onConfirm() {
          addParticipant(values, helpers);
        },
        onCancel() {
          setShowModal(true);
          helpers.setSubmitting(false);
        },
      })
    );
  };

  const addParticipant = async (
    values: AddOrganisationToConversationFormValues,
    { setSubmitting, validateForm, resetForm }: FormikHelpers<AddOrganisationToConversationFormValues>
  ) => {
    setSubmitting(true);
    try {
      await validateForm();

      const organisationId = values.organisation!.key;

      await conversationApi.addConversationParticipant({
        conversationId: conversation.id,
        bridgeApiAddParticipantToConversation: {
          organisationId: organisationId,
        },
      });

      onUpdate(true);
      setShowModal(false);
      resetForm(initialFormState);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SalvModal show={showModal} onHide={() => setShowModal(false)} className='add-organisation-to-conv-modal'>
      <LoadingOverlay loading={loading}>
        <Formik initialValues={initialValues} onSubmit={confirm} validationSchema={validationSchema}>
          {({
            isSubmitting,
            setFieldValue,
            errors,
            values,
            isValid,
          }: FormikProps<AddOrganisationToConversationFormValues>) => {
            return (
              <Form>
                <SalvModal.Header>Add organisation to the conversation </SalvModal.Header>
                <SalvModal.Body>
                  <SalvDropdown
                    name='organisation'
                    options={organisations.map((apiOrganisation) =>
                      convertForSelect(apiOrganisation.name, apiOrganisation.id)
                    )}
                    onChange={(value: DropdownOption[]) => {
                      setFieldValue('organisation', value[0]);
                    }}
                    size={Size.MEDIUM}
                    value={values.organisation}
                    label='Organisation to be added'
                    isInvalid={!!errors.organisation}
                    placeholder='Select...'
                  />
                  <div className='text-s note'>
                    This organisation will get access to the content of this conversation.
                  </div>
                </SalvModal.Body>

                <SalvModal.Footer>
                  <SalvButton
                    size={Size.LARGE}
                    variant='muted'
                    onClick={() => {
                      setShowModal(false);
                    }}
                    label='Cancel'
                  />
                  <SalvButton
                    size={Size.LARGE}
                    variant='primary'
                    type='submit'
                    label='Add'
                    disabled={isSubmitting || !isValid || !values.organisation}
                  />
                </SalvModal.Footer>
              </Form>
            );
          }}
        </Formik>
      </LoadingOverlay>
    </SalvModal>
  );
};

export default AddOrganisationToConversationModal;
